import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Link } from 'react-router-dom';
import {
  CheckOutlined,
  DatabaseOutlined,
  LoadingOutlined,
  PlusOutlined,
  RightOutlined,
} from '@ant-design/icons';
import {
  addPredictionDataVersion,
  deletePredictionVersion,
  getPredictionDataVersions,
  removePredictionDataVersion,
  setPredictionCurrentVersion,
  updatePredictionDataVersion,
  type PredictionDataVersionItem,
} from '@apis/simulation';

/** 技术数据（版本号 / 成功率 / 计数）统一等宽字体，与页面其它列同款。 */
const MONO =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

const SUCCESS_COLOR = '#16a34a';
const WARN_COLOR = '#d97706';
const DANGER_COLOR = '#dc2626';
const MUTED_COLOR = '#9aa0ab';

/** 成功率配色：≥90%（自动进入线）绿、≥60% 橙、其余红。 */
function successColor(pct: number): string {
  if (pct >= 90) return SUCCESS_COLOR;
  if (pct >= 60) return WARN_COLOR;
  return DANGER_COLOR;
}

/**
 * 行内描述编辑：失焦 / 回车即保存（PATCH），保存中转 loading、成功后短暂显示对勾。
 * 本地 draft 与 value 同步；lastSavedRef 防止「回车后 blur」重复提交。
 */
function DescriptionEditor({
  value,
  saving,
  saved,
  onSave,
}: {
  value: string;
  saving: boolean;
  saved: boolean;
  onSave: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const lastSavedRef = useRef(value);

  useEffect(() => {
    setDraft(value);
    lastSavedRef.current = value;
  }, [value]);

  const submit = () => {
    const next = draft.trim();
    if (next === lastSavedRef.current) return;
    lastSavedRef.current = next;
    onSave(next);
  };

  return (
    <Input
      size="small"
      variant="borderless"
      value={draft}
      placeholder="添加描述"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={submit}
      onPressEnter={(e) => {
        submit();
        (e.target as HTMLInputElement).blur();
      }}
      suffix={
        saving ? (
          <LoadingOutlined style={{ fontSize: 12, color: MUTED_COLOR }} />
        ) : saved ? (
          <CheckOutlined style={{ fontSize: 12, color: SUCCESS_COLOR }} />
        ) : null
      }
      style={{ paddingInline: 4, fontSize: 12, color: '#3a3f4b' }}
    />
  );
}

/**
 * 数据更新页顶部「预测数据版本」列表。
 *
 * 口径：成功率 ≥90% 的版本自动进入列表（listed），也可人工「手动加入」；
 * 「创建任务展示」开关控制该版本是否出现在创建任务处的版本下拉。
* 「设为当前 / 移除 / 删除版本」沿用页面既有 version/current + version 接口：
 * 行里缺 predictionVersion / groupTag（老数据 / 尚无迭代记录）时禁用并提示。
 * 「移除」只摘列表 / 创建任务下拉展示，不删预测数据，可「手动加入」恢复；当前生效版本不可移除。
 *
 * fail-soft：列表加载失败只在区内提示 + 重试，不影响下方 Job 列表。
 */
export default function PredictionDataVersionList({
  refreshToken = 0,
  onChanged,
}: {
  /** 父级数据变化（如删除迭代）时自增，触发列表重拉。 */
  refreshToken?: number;
  /** 版本操作（设为当前 / 删除 / 手动加入）成功后通知父级刷新 Job 列表。 */
  onChanged?: () => void;
}) {
  const [items, setItems] = useState<PredictionDataVersionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // 默认只显示 listed（≥90% 自动进入 + 手动加入）；打开后带出未达 90% 的版本。
  const [includeAll, setIncludeAll] = useState(false);
  // 顶部总览可折叠（默认展开），给下方 Job 列表留空间。
  const [open, setOpen] = useState(true);

  // 行内描述保存态
  const [descriptionSaving, setDescriptionSaving] = useState<Set<string>>(new Set());
  const [descriptionSaved, setDescriptionSaved] = useState<string | null>(null);
  const savedTimerRef = useRef<number | null>(null);
  // 「创建任务展示」开关在途态
  const [switchPending, setSwitchPending] = useState<Set<string>>(new Set());
  // 版本操作在途态：`set:<predictionVersion>` / `del:<predictionVersion>`
  const [opKey, setOpKey] = useState<string | null>(null);

  // 手动加入弹窗
  const [addOpen, setAddOpen] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const list = await getPredictionDataVersions(includeAll);
      setItems(list);
    } catch (err) {
      // 业务错误已由 api 层统一提示；这里只做区内降级（不打断下方 Job 列表）
      console.error('加载预测数据版本失败:', err);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [includeAll]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  // 成功对勾 1.5s 后自动消失；卸载时清定时器
  useEffect(
    () => () => {
      if (savedTimerRef.current) window.clearTimeout(savedTimerRef.current);
    },
    [],
  );

  // 打开弹窗时重置表单，避免上次输入残留
  useEffect(() => {
    if (addOpen) addForm.resetFields();
  }, [addOpen, addForm]);

  const flashSaved = (predictionVersion: string) => {
    setDescriptionSaved(predictionVersion);
    if (savedTimerRef.current) window.clearTimeout(savedTimerRef.current);
    savedTimerRef.current = window.setTimeout(() => {
      setDescriptionSaved((cur) => (cur === predictionVersion ? null : cur));
    }, 1500);
  };

  const markPending = (setter: typeof setDescriptionSaving, key: string) => {
    setter((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };
  const clearPending = (setter: typeof setDescriptionSaving, key: string) => {
    setter((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  };

  // 描述保存：成功后就地合并返回行（不整表刷新，避免输入框失焦抖动）
  const handleSaveDescription = async (record: PredictionDataVersionItem, description: string) => {
    const { predictionVersion } = record;
    markPending(setDescriptionSaving, predictionVersion);
    try {
      const updated = await updatePredictionDataVersion(predictionVersion, { description });
      setItems((prev) =>
        prev.map((it) => (it.predictionVersion === predictionVersion ? { ...it, ...updated } : it)),
      );
      flashSaved(predictionVersion);
    } catch (err) {
      console.error('保存版本描述失败:', err);
    } finally {
      clearPending(setDescriptionSaving, predictionVersion);
    }
  };

  // 创建任务展示开关：乐观以外就地合并返回行；失败时状态不变（受控开关自动回退）
  const handleToggleShowInCreateJob = async (
    record: PredictionDataVersionItem,
    checked: boolean,
  ) => {
    const { predictionVersion } = record;
    markPending(setSwitchPending, predictionVersion);
    try {
      const updated = await updatePredictionDataVersion(predictionVersion, { showInCreateJob: checked });
      setItems((prev) =>
        prev.map((it) => (it.predictionVersion === predictionVersion ? { ...it, ...updated } : it)),
      );
    } catch (err) {
      console.error('更新「创建任务展示」失败:', err);
    } finally {
      clearPending(setSwitchPending, predictionVersion);
    }
  };

  // 设为当前版本：口径同原覆盖率卡片（人工设定，dispatch 基线随之指向）
  const handleSetCurrent = async (record: PredictionDataVersionItem) => {
    const predictionVersion = record.predictionVersion;
    if (!predictionVersion) return;
    setOpKey(`set:${predictionVersion}`);
    try {
      const d = await setPredictionCurrentVersion(predictionVersion);
      message.success(
        d.changed
          ? `已将 ${d.predictionVersion} 设为当前版本` +
              (d.previousVersion ? `（原当前版本：${d.previousVersion}）` : '') +
              `，现覆盖 ${d.coveredScenes} 个场景`
          : `${d.predictionVersion} 已是当前版本，无需切换`,
      );
      await load();
      onChanged?.();
    } catch (err) {
      console.error('设置当前版本失败:', err);
    } finally {
      setOpKey(null);
    }
  };

  // 删除版本：按版本号硬删全部预测数据 + BOS 目录（与页面原逻辑同口径）
  const handleDeleteVersion = async (record: PredictionDataVersionItem) => {
    const predictionVersion = record.predictionVersion;
    if (!predictionVersion) return;
    setOpKey(`del:${predictionVersion}`);
    try {
      const d = await deletePredictionVersion(predictionVersion);
      message.success(
        `已删除 ${d.predictionVersion}：${d.deletedRows} 条数据 / 清理 ${d.bosObjects} 个 BOS 对象`,
      );
      if (d.skippedRows > 0) {
        message.warning(`有 ${d.skippedRows} 行 BOS 删除失败已跳过，可重试删除`);
      }
      if (d.wasCurrent) {
        message.warning(
          `${d.predictionVersion} 原为当前生效版本，平台现已无当前版本，请在剩余版本里点「设为当前」重新指定`,
        );
      }
      await load();
      onChanged?.();
    } catch (err) {
      console.error('删除版本失败:', err);
    } finally {
      setOpKey(null);
    }
  };

  // 移除版本：只把版本号从列表摘掉（不删预测数据），之后可「手动加入」恢复
  const handleRemoveVersion = async (record: PredictionDataVersionItem) => {
    const predictionVersion = record.predictionVersion;
    if (!predictionVersion) return;
    setOpKey(`rm:${predictionVersion}`);
    try {
      await removePredictionDataVersion(predictionVersion);
      message.success(
        `已将 ${predictionVersion} 从列表移除（预测数据保留，可用「手动加入」恢复）`,
      );
      await load();
      onChanged?.();
    } catch (err) {
      console.error('移除版本失败:', err);
    } finally {
      setOpKey(null);
    }
  };

  const handleAdd = async () => {
    let values: { predictionVersion?: string; description?: string };
    try {
      values = await addForm.validateFields();
    } catch {
      return; // 校验失败，Form 已就地标红
    }
    const predictionVersion = String(values.predictionVersion ?? '').trim();
    if (!predictionVersion) return;
    setAddSubmitting(true);
    try {
      await addPredictionDataVersion({
        predictionVersion,
        description: values.description?.trim() || undefined,
      });
      message.success(`已加入版本 ${predictionVersion}`);
      setAddOpen(false);
      addForm.resetFields();
      await load();
      onChanged?.();
    } catch (err) {
      console.error('手动加入版本失败:', err);
    } finally {
      setAddSubmitting(false);
    }
  };

  const columns: ColumnsType<PredictionDataVersionItem> = [
    {
      title: '预测数据版本',
      dataIndex: 'predictionVersion',
      key: 'predictionVersion',
      width: 280,
      render: (text: string, record) => {
        const versionText = (
          <Typography.Text
            copyable={{ text }}
            style={{ fontFamily: MONO, fontSize: 12, wordBreak: 'break-all' }}
          >
            {record.latestJobId ? (
              // 跳到产出该版本的最近一个 Job 的任务列表（补跑过的版本取最新一批）
              <Link
                to={`/prediction-update/job-tasks/${record.latestJobId}`}
                title={`查看产出该版本的 Job #${record.latestJobId}`}
                style={{ color: '#5e6ad2' }}
              >
                {text}
              </Link>
            ) : (
              text
            )}
          </Typography.Text>
        );
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            {versionText}
            {record.isCurrent && (
              <Tag
                style={{
                  margin: 0,
                  flexShrink: 0,
                  fontSize: 11,
                  lineHeight: '17px',
                  color: SUCCESS_COLOR,
                  background: '#eafaf0',
                  border: '1px solid #d3f0de',
                }}
              >
                当前
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: '成功率',
      dataIndex: 'successRate',
      key: 'successRate',
      width: 150,
      render: (_, record) => {
        if (!record.total) {
          return <span style={{ color: '#c4c6cc' }}>—</span>;
        }
        const pct = Math.round((record.successRate ?? 0) * 100);
        const color = successColor(pct);
        return (
          <Tooltip title={`成功 ${record.succeeded} / 共 ${record.total} 个场景`}>
            <span
              style={{
                fontFamily: MONO,
                fontVariantNumeric: 'tabular-nums',
                whiteSpace: 'nowrap',
              }}
            >
              <b style={{ color, fontSize: 13 }}>{pct}%</b>
              <span style={{ color: MUTED_COLOR, marginLeft: 6, fontSize: 11 }}>
                {record.succeeded}/{record.total}
              </span>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: '覆盖场景',
      dataIndex: 'coveredScenes',
      key: 'coveredScenes',
      width: 95,
      align: 'center',
      render: (value: number) => (
        <Tooltip title="该版本已覆盖的场景数（已有预测数据的场景）">
          <span
            style={{
              fontFamily: MONO,
              fontVariantNumeric: 'tabular-nums',
              fontSize: 12,
              color: value > 0 ? '#3a3f4b' : '#c4c6cc',
            }}
          >
            {value ?? 0}
          </span>
        </Tooltip>
      ),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 240,
      render: (_, record) => (
        <DescriptionEditor
          value={record.description ?? ''}
          saving={descriptionSaving.has(record.predictionVersion)}
          saved={descriptionSaved === record.predictionVersion}
          onSave={(next) => handleSaveDescription(record, next)}
        />
      ),
    },
    {
      title: '创建任务展示',
      dataIndex: 'showInCreateJob',
      key: 'showInCreateJob',
      width: 110,
      align: 'center',
      render: (_, record) => (
        <Tooltip title="开启后，该版本出现在创建任务处的预测数据版本下拉中">
          <Switch
            size="small"
            checked={!!record.showInCreateJob}
            loading={switchPending.has(record.predictionVersion)}
            onChange={(checked) => handleToggleShowInCreateJob(record, checked)}
          />
        </Tooltip>
      ),
    },
    {
      title: '来源',
      dataIndex: 'manuallyAdded',
      key: 'manuallyAdded',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Space size={4} wrap>
          {record.manuallyAdded ? (
            <Tag
              style={{
                margin: 0,
                fontSize: 11,
                lineHeight: '18px',
                color: '#5e6ad2',
                background: '#f1f2fb',
                border: '1px solid #e3e5f7',
              }}
            >
              手动
            </Tag>
          ) : (
            <Tag
              style={{
                margin: 0,
                fontSize: 11,
                lineHeight: '18px',
                color: '#6b7280',
                background: '#f4f5f7',
                border: '1px solid #e9eaee',
              }}
            >
              自动
            </Tag>
          )}
          {!record.listed && (
            <Tooltip title="成功率未达 90%，仅勾选「显示全部」时可见">
              <Tag
                style={{
                  margin: 0,
                  fontSize: 11,
                  lineHeight: '18px',
                  color: '#9aa0ab',
                  background: 'transparent',
                  border: '1px dashed #d8dbe0',
                }}
              >
                未达 90%
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 230,
      align: 'center',
      render: (_, record) => {
// 行里缺版本标识（老数据 / 尚无迭代记录）→ 设为当前 / 删除禁用并提示；移除不受限
        const hasIdentity = !!record.predictionVersion && !!record.groupTag;
        const emptyVersion = (record.coveredScenes ?? 0) <= 0;
        const setBusy = opKey === `set:${record.predictionVersion}`;
        const removeBusy = opKey === `rm:${record.predictionVersion}`;
        const deleteBusy = opKey === `del:${record.predictionVersion}`;

        const setTooltip = !hasIdentity
          ? '该版本尚无关联迭代记录（缺少 predictionVersion / groupTag），无法设为当前'
          : record.isCurrent
            ? '已是当前生效版本'
            : emptyVersion
              ? '该版本还没有任何预测数据，不能设为当前版本（可先补跑缺失场景）'
              : '把该版本提为当前生效版本（全平台唯一），dispatch 基线随之指向它';

        const removeTooltip = record.isCurrent
          ? '当前生效版本不可移除（列表是切换当前版本的唯一入口），请先设为其它版本或删除版本'
          : '只把版本号从列表与创建任务下拉中移除，不删预测数据；之后可「手动加入」恢复';

        return (
          <Space size={8}>
            <Tooltip title={setTooltip}>
              {/* disabled 的 Button 不派发鼠标事件、Tooltip 会失效，故包一层 span 接住悬浮 */}
              <span>
                <Button
                  size="small"
                  type="link"
                  style={{ padding: 0, height: 'auto', fontSize: 12 }}
                  disabled={!hasIdentity || !!record.isCurrent || emptyVersion}
                  loading={setBusy}
                  onClick={() => handleSetCurrent(record)}
                >
                  设为当前
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={removeTooltip}>
              <span>
                <Button
                  size="small"
                  type="link"
                  style={{ padding: 0, height: 'auto', fontSize: 12 }}
                  disabled={!!record.isCurrent}
                  loading={removeBusy}
                  onClick={() => handleRemoveVersion(record)}
                >
                  移除
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                hasIdentity
                  ? undefined
                  : '该版本尚无关联迭代记录（缺少 predictionVersion / groupTag），无法删除'
              }
            >
              <span>
                <Popconfirm
                  title="确认删除版本"
                  disabled={!hasIdentity}
                  description={
                    <div style={{ maxWidth: 280 }}>
                      将同时删除该版本（{record.predictionVersion}）在 BOS
                      上的全部预测数据（含 PG 记录，现覆盖 {record.coveredScenes} 个场景），不可恢复。
                      若该版本正被在跑的批次当基线读取，删除会使其预测数据读不到 —— 确认无在跑批次再删。
                      {record.isCurrent &&
                        '该版本是当前生效版本（全平台唯一），删除后平台将暂无当前版本，需要重新指定。'}
                      确定删除？
                    </div>
                  }
                  onConfirm={() => handleDeleteVersion(record)}
                  okText="删除"
                  okButtonProps={{ danger: true }}
                  cancelText="取消"
                >
                  <Button
                    size="small"
                    type="link"
                    danger
                    style={{ padding: 0, height: 'auto', fontSize: 12 }}
                    disabled={!hasIdentity}
                    loading={deleteBusy}
                  >
                    删除版本
                  </Button>
                </Popconfirm>
              </span>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #ebebeb',
        borderRadius: 10,
        // 父级 .layout-container 是 height:100% 的 flex column：不加 flexShrink:0 会被压缩
        flexShrink: 0,
      }}
    >
      {/* 常驻表头：标题 / 副标题 + 右侧开关 / 手动加入 + 折叠箭头（点整行切换展开） */}
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <DatabaseOutlined style={{ color: '#5e6ad2', fontSize: 16 }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
          <span
            style={{ fontSize: 15, fontWeight: 600, color: '#1f2430', letterSpacing: '.01em' }}
          >
            预测数据版本
          </span>
          <span style={{ fontSize: 12, color: MUTED_COLOR }}>
            成功率 ≥90% 自动进入；移除只摘展示不删数据，可随时「手动加入」恢复
          </span>
        </div>
        {!loading && !loadError && (
          <span
            style={{
              flexShrink: 0,
              fontFamily: MONO,
              fontSize: 11,
              color: '#8b93e6',
              background: '#f1f2fb',
              padding: '1px 8px',
              borderRadius: 999,
            }}
          >
            {items.length} 个版本
          </span>
        )}
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 12,
              color: '#6b7280',
            }}
          >
            <Switch size="small" checked={includeAll} onChange={setIncludeAll} />
            {'显示全部（含 <90%）'}
          </span>
          <Button
            size="small"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAddOpen(true)}
          >
            手动加入
          </Button>
        </div>
        <RightOutlined
          style={{
            color: MUTED_COLOR,
            fontSize: 12,
            flexShrink: 0,
            transition: 'transform .2s ease',
            transform: open ? 'rotate(90deg)' : 'none',
          }}
        />
      </div>

      {open && (
        <div style={{ padding: '0 16px 14px', borderTop: '1px solid #f0f0f0' }}>
          <div style={{ paddingTop: 10 }}>
            {loadError && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 10,
                  padding: '6px 10px',
                  borderRadius: 6,
                  background: '#fdf0e1',
                  color: WARN_COLOR,
                  fontSize: 12,
                }}
              >
                版本列表加载失败，可稍后重试（不影响下方 Job 列表）
                <Button
                  size="small"
                  type="link"
                  style={{ padding: 0, height: 'auto', fontSize: 12 }}
                  onClick={() => load()}
                >
                  重试
                </Button>
              </div>
            )}
            <Table<PredictionDataVersionItem>
              rowKey="predictionVersion"
              size="small"
              dataSource={items}
              columns={columns}
              loading={loading}
              tableLayout="fixed"
              scroll={{ x: 1225 }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <span style={{ fontSize: 12, color: MUTED_COLOR }}>
                        暂无预测数据版本（成功率 ≥90% 的版本会自动进入，也可手动加入）
                      </span>
                    }
                  />
                ),
              }}
              pagination={{
                pageSize: 10,
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (t) => `共 ${t} 个版本`,
              }}
            />
          </div>
        </div>
      )}

      {/* 手动加入弹窗：版本号必填（手填，如 AD3.1.3 / release.10.12.34），描述可选 */}
      <Modal
        title="手动加入预测数据版本"
        open={addOpen}
        onCancel={() => {
          setAddOpen(false);
          addForm.resetFields();
        }}
        onOk={handleAdd}
        okText="加入"
        cancelText="取消"
        confirmLoading={addSubmitting}
        width={520}
      >
        <Form form={addForm} layout="vertical" preserve={false} style={{ marginTop: 12 }}>
          <Form.Item
            label="预测数据版本"
            name="predictionVersion"
            rules={[{ required: true, whitespace: true, message: '请输入预测数据版本' }]}
          >
            <Input
              placeholder="例如 prediction.AD3.1.3 / prediction.release.10.12.34 / prediction.orin_dev"
              allowClear
            />
          </Form.Item>
          <Form.Item label="描述" name="description">
            <Input placeholder="可选，用于说明该版本用途 / 来源" allowClear />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}